import React from 'react';
import { useTranslation } from 'react-i18next';
import { Task, Opportunity } from '../../types';
import { Calendar, Clock, Target, CheckCircle } from 'lucide-react';

interface UpcomingCalendarProps {
  tasks: Task[];
  opportunities: Opportunity[];
  onNavigateToTask?: (taskId: string) => void;
  onNavigateToOpportunity?: (opportunityId: string) => void;
}

const UpcomingCalendar: React.FC<UpcomingCalendarProps> = ({ tasks, opportunities, onNavigateToTask, onNavigateToOpportunity }) => {
  const { t, i18n } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const next7Days = new Date(today);
  next7Days.setDate(next7Days.getDate() + 7);

  // Upcoming tasks
  const upcomingTasks = tasks.filter(t => 
    !t.completed && 
    new Date(t.dueDate) >= today && 
    new Date(t.dueDate) <= next7Days
  ).sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()).slice(0, 5);

  // Opportunities closing soon
  const closingOpps = opportunities.filter(o => 
    !['won', 'lost'].includes(o.stage) &&
    new Date(o.expectedCloseDate) >= today &&
    new Date(o.expectedCloseDate) <= next7Days
  ).sort((a, b) => new Date(a.expectedCloseDate).getTime() - new Date(b.expectedCloseDate).getTime()).slice(0, 3);

  const formatDate = (date: Date) => {
    const d = new Date(date);
    const diffDays = Math.ceil((d.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
    const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
    
    if (diffDays === 0) return tc('today');
    if (diffDays === 1) return tc('tomorrow');
    return d.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const allItems = [
    ...upcomingTasks.map(t => ({
      id: t.id,
      type: 'task' as const,
      title: t.title,
      date: new Date(t.dueDate),
      icon: t.type === 'meeting' ? Calendar : CheckCircle,
      color: 'text-accent'
    })),
    ...closingOpps.map(o => ({
      id: o.id,
      type: 'opportunity' as const,
      title: o.title,
      date: new Date(o.expectedCloseDate),
      icon: Target,
      color: 'text-accent'
    }))
  ].sort((a, b) => a.date.getTime() - b.date.getTime()).slice(0, 7);

  return (
    <div className="bg-bg-card/90 backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-card border-0 p-4 sm:p-6 hover:shadow-card transition-shadow duration-200">
      <div className="flex items-center space-x-2 mb-4">
        <div className="p-2 bg-warning-soft rounded-lg">
          <Calendar className="w-5 h-5 text-warning" />
        </div>
        <h3 className="text-lg font-semibold text-txt">{t('calendar.thisWeek')}</h3>
      </div>

      <div>
        {allItems.length === 0 ? (
          <div className="text-center py-6">
            <Calendar className="w-10 h-10 text-txt-muted mx-auto mb-2" />
            <p className="text-sm text-txt-tertiary">{t('calendar.noEvents')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {allItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  if (item.type === 'task') onNavigateToTask?.(item.id);
                  else onNavigateToOpportunity?.(item.id);
                }}
                className="w-full flex items-center space-x-3 p-2 bg-bg-surface rounded-lg hover:bg-bg-input hover:-translate-y-0.5 transition-all cursor-pointer text-left"
              >
                <item.icon className={`w-4 h-4 ${item.color} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-txt truncate">{item.title}</div>
                  <div className="text-xs text-txt-secondary">{formatDate(item.date)}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingCalendar;
